import axios from 'axios'
import { isInIframe, refreshParentToken } from '../utils/notifyParent'

// API base URL - runtime config is handled by ConfigContext,
// this default works because the app is served from the same origin as the API
const apiBaseURL = '/api/v1'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: apiBaseURL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Store the auth hook getter (will be set by App component)
let getAccessTokenFn = null

export const setAccessTokenGetter = (fn) => {
  getAccessTokenFn = fn
}

// Handler invoked when a 401 cannot be recovered (token invalid/expired/missing).
// Set by App; clears the stale session so the app drops back to the login flow.
let onAuthFailure = null

export const setAuthFailureHandler = (fn) => {
  onAuthFailure = fn
}

// Guard so a burst of concurrent 401s triggers the re-auth flow only once.
let authFailureTriggered = false

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    try {
      // Use the auth hook's getAccessToken method if available
      if (getAccessTokenFn) {
        const token = await getAccessTokenFn()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      } else {
        // Fallback to localStorage (for initial load)
        const token = localStorage.getItem('agentflow_jwt')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      }
    } catch (error) {
      console.error('Failed to get access token:', error)
      // Fallback: check storage directly when token getter fails (e.g. runtimeConfig not yet loaded)
      const token = localStorage.getItem('agentflow_jwt') || sessionStorage.getItem('agentflow_parent_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }
    return config
  },
  (error) => {
    console.error('API request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor - Shows modal instead of redirects
api.interceptors.response.use(
  (response) => {
    // A successful call means the token is valid again — re-arm the 401 guard.
    authFailureTriggered = false
    return response
  },
  async (error) => {
    const status = error.response?.status

    // Handle 401 errors (invalid/expired token)
    if (status === 401) {
      // In iframe with parent token: try refreshing and retrying once
      if (!error.config._retried && isInIframe() && sessionStorage.getItem('agentflow_parent_token')) {
        error.config._retried = true
        try {
          await refreshParentToken()
          return api(error.config)
        } catch {
          // Refresh failed, fall through to normal error handling
        }
      }

      // Standalone: an unrecoverable 401 means the token is invalid/expired/missing
      // on ANY endpoint, not just auth ones. Trigger the re-auth flow instead of
      // failing silently — the handler clears the stale session, which routes the
      // app to the login screen (and re-auth). Iframe auth is owned by the parent
      // app, so leave that path to its own handlers.
      if (!isInIframe() && !authFailureTriggered) {
        authFailureTriggered = true
        if (onAuthFailure) {
          onAuthFailure()
        }
      }
    }

    // Handle 403 errors (insufficient permissions) - DON'T show modal
    // These are resource-specific permission errors, not global auth failures
    // Let the component display appropriate error messages

    return Promise.reject(error)
  }
)

// Auth API methods
export const authAPI = {
  validateToken: async () => {
    // Call the auth validation endpoint which syncs user to database
    // This endpoint is specifically designed for authentication validation
    // and will create/update the user in the database on every login
    const response = await api.get('/auth/validate')
    return response.data
  },

  parseJWT: (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload
    } catch (error) {
      console.error('Failed to parse JWT token:', error)
      return null
    }
  },
}

// Tickets API methods
export const ticketsAPI = {
  getTickets: async () => {
    const response = await api.get('/tickets/')
    return response.data
  },
  
  getTicketSummaries: async () => {
    const response = await api.get('/tickets/summaries')
    return response.data
  },

  // New unified API - gets summaries with optional expanded ticket details
  // Supports pagination and server-side filtering
  getUnifiedTickets: async (selectedTicketId = null, options = {}) => {
    const {
      page = 1,
      page_size = 20,
      status,
      ticket_type,
      assigned,
      created,
      archived_only,
      include_archived,
      include_logs
    } = options

    const params = new URLSearchParams()
    if (selectedTicketId) {
      params.append('selected_ticket_id', selectedTicketId)
    }
    params.append('page', page.toString())
    params.append('page_size', page_size.toString())

    // Add filters
    if (status) params.append('status', status)
    if (ticket_type) params.append('ticket_type', ticket_type)
    if (assigned !== undefined && assigned !== null) params.append('assigned', assigned.toString())
    if (created !== undefined && created !== null) params.append('created', created.toString())
    if (archived_only) params.append('archived_only', 'true')
    if (include_archived) params.append('include_archived', 'true')
    if (include_logs) params.append('include_logs', 'true')

    const queryString = params.toString()
    const url = `/tickets/unified${queryString ? `?${queryString}` : ''}`
    const response = await api.get(url)
    return response.data
  },

  // Lightweight list API - returns minimal fields for list display
  // Supports pagination, filtering, and server-side search
  getTicketList: async (options = {}) => {
    const {
      selectedTicketId = null,
      query = null,
      page = 1,
      page_size = 20,
      status,
      ticket_type,
      assigned,
      created,
      archived_only,
      include_archived,
      filter_user
    } = options

    const params = new URLSearchParams()
    if (selectedTicketId) {
      params.append('selected_ticket_id', selectedTicketId)
    }
    if (query) {
      params.append('query', query)
    }
    params.append('page', page.toString())
    params.append('page_size', page_size.toString())

    // Add filters
    if (status) params.append('status', status)
    if (ticket_type) params.append('ticket_type', ticket_type)
    if (assigned !== undefined && assigned !== null) params.append('assigned', assigned.toString())
    if (created !== undefined && created !== null) params.append('created', created.toString())
    if (archived_only) params.append('archived_only', 'true')
    if (include_archived) params.append('include_archived', 'true')
    if (filter_user) params.append('filter_user', filter_user)

    const queryString = params.toString()
    const url = `/tickets/list${queryString ? `?${queryString}` : ''}`
    const response = await api.get(url)
    return response.data
  },

  getTicket: async (ticketId) => {
    const response = await api.get(`/tickets/${ticketId}`)
    return response.data
  },
  
  createTicket: async (ticketData) => {
    const response = await api.post('/tickets/', ticketData)
    return response.data
  },
  
  deleteTicket: async (ticketId) => {
    const response = await api.delete(`/tickets/${ticketId}`)
    return response.data
  },

  updateTicket: async (ticketId, updates) => {
    const response = await api.patch(`/tickets/${ticketId}`, updates)
    return response.data
  },

  archiveTicket: async (ticketId) => {
    const response = await api.post(`/tickets/${ticketId}/archive`)
    return response.data
  },

  submitUserResponse: async (ticketId, responseData) => {
    const response = await api.post(`/tickets/${ticketId}/user-response`, responseData)
    return response.data
  },
  
  uploadFileToTicket: async (ticketId, formData) => {
    const response = await api.post(`/files/ticket/${ticketId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },
  
  submitExternalResponse: async (ticketId, responseData) => {
    const response = await api.post(`/tickets/${ticketId}/external-response`, responseData)
    return response.data
  },
  
  getTicketTypes: async () => {
    const response = await api.get('/tickets/types')
    return response.data
  },
  
  getTicketSchema: async (ticketType) => {
    const response = await api.get(`/tickets/schema/${ticketType}`)
    return response.data
  },

  // Notes API methods
  getTicketNotes: async (ticketId) => {
    const response = await api.get(`/tickets/${ticketId}/notes`)
    return response.data
  },

  addTicketNote: async (ticketId, messageIndex, note) => {
    const response = await api.post(`/tickets/${ticketId}/notes`, {
      message_index: messageIndex,
      note: note
    })
    return response.data
  },

  translateTicket: async (ticketId, targetLanguage) => {
    const response = await api.post(`/tickets/${ticketId}/translate`, {
      target_language: targetLanguage
    })
    return response.data
  },
}

// Agents API methods
export const agentsAPI = {
  getAgents: async (minimal = false, includeSubAgents = false) => {
    const params = new URLSearchParams()
    if (minimal) params.append('minimal', 'true')
    if (includeSubAgents) params.append('include_sub_agents', 'true')
    const queryString = params.toString()
    const url = `/agents/${queryString ? `?${queryString}` : ''}`
    const response = await api.get(url)
    return response.data
  },
  
  getAgentInfo: async (agentType) => {
    const response = await api.get(`/agents/${agentType}`)
    return response.data
  },
  
  getAgentCapabilities: async (agentType) => {
    const response = await api.get(`/agents/${agentType}/capabilities`)
    return response.data
  },
  
  refreshAgentSchemas: async () => {
    const response = await api.post('/agents/refresh')
    return response.data
  },

  getSubAgents: async (agentName) => {
    const response = await api.get(`/agents/${agentName}/sub-agents`)
    return response.data
  },

  processTicket: async (ticketId) => {
    const response = await api.post(`/tickets/${ticketId}/process`)
    return response.data
  },
}

// Test Runs API methods
export const testRunsAPI = {
  getTestRuns: async () => {
    const response = await api.get('/test-runs/')
    return response.data
  },
  
  getTestRunDetails: async (runId) => {
    const response = await api.get(`/test-runs/${runId}`)
    return response.data
  },
  
  getTestRunReport: async (runId) => {
    const response = await api.get(`/test-runs/${runId}/report`)
    return response.data
  },
}

// Email Management API methods
export const emailAPI = {
  getStatus: async () => {
    const response = await api.get('/emails/status')
    return response.data
  },
  
  getPollingStatus: async () => {
    const response = await api.get('/emails/polling/status')
    return response.data
  },
  
  triggerPoll: async () => {
    const response = await api.post('/emails/polling/trigger')
    return response.data
  },
  
  fetchEmails: async (options = {}) => {
    const response = await api.post('/emails/fetch', {
      folder: options.folder || 'inbox',
      max_emails: options.max_emails || 50,
      unread_only: options.unread_only !== false
    })
    return response.data
  },
  
  testConnection: async () => {
    const response = await api.post('/emails/test')
    return response.data
  },
  
  getListeners: async () => {
    const response = await api.get('/emails/listeners')
    return response.data
  },
}

// Memory API functions
export const memoryAPI = {
  getGroups: async () => {
    const response = await api.get('/memory/groups')
    return response.data
  },
  
  getKeys: async (group, page = 1, limit = 50) => {
    const response = await api.get(`/memory/groups/${encodeURIComponent(group)}/keys`, {
      params: { page, limit }
    })
    return response.data
  },
  
  getData: async (group, key) => {
    const response = await api.get(`/memory/groups/${encodeURIComponent(group)}/keys/${encodeURIComponent(key)}`)
    return response.data
  },
  
  deleteKey: async (group, key) => {
    const response = await api.delete(`/memory/groups/${encodeURIComponent(group)}/keys/${encodeURIComponent(key)}`)
    return response.data
  },
  
  deleteGroup: async (group) => {
    const response = await api.delete(`/memory/groups/${encodeURIComponent(group)}`)
    return response.data
  },
}

// Analytics API methods
export const analyticsAPI = {
  getTicketCounts: async (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value)
      }
    })
    const queryString = params.toString()
    const url = `/analytics/ticket-counts${queryString ? `?${queryString}` : ''}`
    const response = await api.get(url)
    return response.data
  },

  getAverageCompletionTime: async (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value)
      }
    })
    const queryString = params.toString()
    const url = `/analytics/average-completion-time${queryString ? `?${queryString}` : ''}`
    const response = await api.get(url)
    return response.data
  },

  getStaleTickets: async (filters = {}, page = 1, pageSize = 20) => {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('page_size', pageSize.toString())
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value)
      }
    })
    const queryString = params.toString()
    const url = `/analytics/stale-tickets${queryString ? `?${queryString}` : ''}`
    const response = await api.get(url)
    return response.data
  },

  getAgentPerformance: async (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value)
      }
    })
    const queryString = params.toString()
    const url = `/analytics/agent-performance${queryString ? `?${queryString}` : ''}`
    const response = await api.get(url)
    return response.data
  },

  getTicketDistribution: async (filters = {}) => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value)
      }
    })
    const queryString = params.toString()
    const url = `/analytics/ticket-distribution${queryString ? `?${queryString}` : ''}`
    const response = await api.get(url)
    return response.data
  },

  getRecentActivity: async (hours = 24, limit = 20) => {
    const params = new URLSearchParams()
    params.append('hours', hours.toString())
    params.append('limit', limit.toString())
    const url = `/analytics/recent-activity?${params.toString()}`
    const response = await api.get(url)
    return response.data
  },

  getAvailableFilters: async () => {
    const response = await api.get('/analytics/available-filters')
    return response.data
  },
}

// Admin API methods
export const adminAPI = {
  getEnvironmentVariables: () => api.get('/admin/environment').then(res => res.data),
  getEnvironmentSummary: () => api.get('/admin/environment/summary').then(res => res.data),
  testAzureStorage: () => api.post('/admin/storage/test').then(res => res.data),
  getAzureCredentialInfo: () => api.get('/admin/azure/credentials').then(res => res.data),
  getAvailableLLMModels: () => api.get('/admin/llm/models').then(res => res.data),
  updateLLMDefaults: (data) => api.put('/admin/llm/defaults', data).then(res => res.data),
  testLLMService: (modelName = null) => {
    const params = modelName ? `?model_name=${encodeURIComponent(modelName)}` : ''
    return api.get(`/admin/llm/test${params}`).then(res => res.data)
  },
  testIzziInternal: (transactionId) => api.get(`/admin/izzi/internal/test?transaction_id=${transactionId}`).then(res => res.data),
  testIzziExternal: () => api.get('/admin/izzi/external/test').then(res => res.data),
  sendTestEmail: () => api.post('/admin/email/test').then(res => res.data),
  getEmailTestStatus: () => api.get('/admin/email/test/status').then(res => res.data),
  cancelEmailTest: () => api.delete('/admin/email/test').then(res => res.data),
  testOCRService: () => api.get('/admin/ocr/test').then(res => res.data),
  testErrorLogging: () => api.post('/admin/logging/test-error').then(res => res.data),
  testAzureMaps: () => api.get('/admin/maps/azure/test').then(res => res.data),
  testGoogleMaps: () => api.get('/admin/maps/google/test').then(res => res.data),

  // Profiling API
  getProfilingStatus: () => api.get('/admin/profiling/status').then(res => res.data),
  enableProfiling: () => api.post('/admin/profiling/enable').then(res => res.data),
  disableProfiling: () => api.post('/admin/profiling/disable').then(res => res.data),
  startProfiling: () => api.post('/admin/profiling/start').then(res => res.data),
  stopProfiling: () => api.post('/admin/profiling/stop').then(res => res.data),
  resetProfiling: () => api.post('/admin/profiling/reset').then(res => res.data),
  getProfilingSummary: (detailed = false) => api.get(`/admin/profiling/summary?detailed=${detailed}`).then(res => res.data),
  getProfilingEntries: (category) => api.get(`/admin/profiling/entries/${encodeURIComponent(category)}`).then(res => res.data),
}

// Cluster API methods
export const clusterAPI = {
  getStatus: async () => {
    const response = await api.get('/cluster/status')
    return response.data
  },

  getJob: async (jobName) => {
    const response = await api.get(`/cluster/jobs/${jobName}`)
    return response.data
  },

  triggerJob: async (jobName) => {
    const response = await api.post(`/cluster/jobs/${jobName}/trigger`)
    return response.data
  },

  forceElection: async () => {
    const response = await api.post('/cluster/master/force-election')
    return response.data
  },

  cleanupNodes: async () => {
    const response = await api.post('/cluster/nodes/cleanup')
    return response.data
  },
}

// Users API methods
export const usersAPI = {
  getCurrentUser: async () => {
    const response = await api.get('/users/me')
    return response.data
  },

  listUsers: async (filters = {}, page = 1, pageSize = 20) => {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('page_size', pageSize.toString())
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value)
      }
    })
    const queryString = params.toString()
    const url = `/users${queryString ? `?${queryString}` : ''}`
    const response = await api.get(url)
    return response.data
  },

  getUser: async (email) => {
    const response = await api.get(`/users/${encodeURIComponent(email)}`)
    return response.data
  },

  createUser: async (userData) => {
    const response = await api.post('/users', userData)
    return response.data
  },

  updateUser: async (email, updates) => {
    const response = await api.patch(`/users/${encodeURIComponent(email)}`, updates)
    return response.data
  },

  getAvailableApplicationRoles: async () => {
    const response = await api.get('/application-roles')
    return response.data
  },

  deleteUser: async (email) => {
    const response = await api.delete(`/users/${encodeURIComponent(email)}`)
    return response.data
  },

  bulkUpsertUsers: async (payload) => {
    const response = await api.post('/users/bulk', payload, { timeout: 300000 })
    return response.data
  },
}

export const filesAPI = {
  uploadFile: async (file, options = {}) => {
    const { agentType, ticketId } = options
    const formData = new FormData()
    formData.append('file', file)

    // Add optional parameters
    if (agentType) {
      formData.append('agent_type', agentType)
    }
    if (ticketId) {
      formData.append('ticket_id', ticketId.toString())
    }

    // Create a custom axios instance for file upload to avoid JSON content-type
    const uploadApi = axios.create({
      baseURL: apiBaseURL,
      timeout: 120000, // 2 minutes for large files
    })

    // Add auth token manually
    const token = await getAccessTokenFn?.() || localStorage.getItem('agentflow_jwt')
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(token && { Authorization: `Bearer ${token}` })
      }
    }

    const response = await uploadApi.post('/files/upload', formData, config)
    return response.data
  },

  getUploadInfo: async () => {
    const response = await api.get('/files/upload/info')
    return response.data
  }
}

/**
 * Retrieve the current access token using the same strategy as the axios interceptor.
 * Falls back to stored tokens for dev mode or when auth context is not yet ready.
 *
 * @returns {Promise<string>} Access token string (may be empty if not authenticated)
 */
export const getAccessToken = async () => {
  if (getAccessTokenFn) {
    try {
      return (await getAccessTokenFn()) || ''
    } catch {
      // Ignore — auth context may not be ready yet
    }
  }
  return (
    localStorage.getItem('agentflow_jwt') ||
    sessionStorage.getItem('agentflow_parent_token') ||
    ''
  )
}

/**
 * Build the full URL for an SSE endpoint.
 *
 * @param {string} path - API path relative to the base URL, e.g. '/tickets/{id}/user-response-stream'
 * @returns {string} Full URL (no token — token is sent via Authorization header)
 */
export const buildSSEUrl = (path) => {
  const base = apiBaseURL.replace(/\/$/, '')
  return `${base}${path}`
}

export default api